import { Address } from "@/dto/address";
import { useEffect, useRef, useState } from "react";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { useSearchParams } from "next/navigation";
import { OrderType } from "@/constants/enum";
import { MerchantLogo } from "./merchantLogo";
import { MerchantNameAndAddress } from "./merchantNameAndAddress";
import { useNativeActions } from "@/utils/useNativeActions";

export interface HeaderMerchantInfoContainerProps {
  merchantLogo?: string;
  merchantName?: string;
  isSimpleStorefront: boolean;
  isStandaloneGiftCard: boolean;
  merchantAddress: Address;
  merchantPhone: string;
  onLogoLoad?: () => void;
}

export const HeaderMerchantInfoContainer = ({
  merchantLogo,
  merchantName,
  isSimpleStorefront,
  isStandaloneGiftCard,
  merchantAddress,
  merchantPhone,
  onLogoLoad,
}: HeaderMerchantInfoContainerProps) => {
  const [isMultiline, setIsMultiline] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const { isCmaApp } = useNativeActions();
  const isDineIn =
    searchParams?.get("order_type")?.toLocaleLowerCase() ===
    OrderType.DINE_IN.toLocaleLowerCase();

  const showLink = !isDineIn && !isStandaloneGiftCard;
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const scanToOrderInfo = getFromLocalStorage<ScanToOrderInfo | null>(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    null
  );
  const pathname = "/";
  const queryParams = {
    order_type: scanToOrderInfo?.orderType,
    table_id: scanToOrderInfo?.tableId,
    employee_id: scanToOrderInfo?.employeeId,
  };
  const linkHref = {
    pathname: pathname,
    ...(scanToOrderInfo && { query: queryParams }),
  };

  useEffect(() => {
    const checkMultiline = () => {
      if (textRef.current) {
        const element = textRef.current;
        const style = globalThis.getComputedStyle(element);
        const lineHeight = Number.parseInt(style.lineHeight, 10);
        const elementHeight = element.clientHeight;
        setIsMultiline(elementHeight > lineHeight);
      }
    };

    checkMultiline();

    window.addEventListener("resize", checkMultiline);

    return () => {
      window.removeEventListener("resize", checkMultiline);
    };
  }, []);

  const logoNode = (
    <MerchantLogo
      className={isCmaApp ? "shrink-0" : ""}
      merchantLogo={merchantLogo}
      showLink={isCmaApp ? false : showLink}
      linkHref={linkHref}
      onImageLoad={onLogoLoad}
    />
  );

  return (
    <div
      className={[
        "flex",
        isSimpleStorefront && !isCmaApp ? "flex-col" : "",
        "sm:flex-row",
        "gap-4",
        "sm:gap-6",
        "items-center",
        isCmaApp ? "min-w-0 max-w-full justify-center flex-row" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {!isCmaApp && logoNode}

      <MerchantNameAndAddress
        logoNode={isCmaApp ? logoNode : undefined}
        showLink={showLink}
        isSimpleStorefront={isSimpleStorefront}
        isStandaloneGiftCard={isStandaloneGiftCard}
        linkHref={linkHref}
        merchantAddress={merchantAddress}
        merchantPhone={merchantPhone}
        textRef={textRef}
        merchantName={merchantName}
        isMultiline={isMultiline}
      />
    </div>
  );
};
